import { generateGuid } from 'global/utils';
import update from 'immutability-helper';
import uniq from 'lodash/uniq';
import { SECTION_ACCESS_TYPES, VIEW_ONLY_SECTIONS } from '../utils/consts';
import {
    ACTIVE_MEMBER_TYPE_CHANGED,
    ADD_FUTURE_CHANGED,
    ADD_MESSAGE_TO_GROUP,
    APP_SELECTION_CHANGE,
    APP_SITE_SELECTION_CHANGE,
    CLEAN_MESSAGES,
    CLOSE_AGENCY_DIALOG,
    CLOSE_CONFIGURATION_REQUIRED_DIALOG,
    AGENCIES_LEGACY_FETCHING,
    SET_SECTION_OVERALL_TOTAL,
    CREATE_NEW_MEMBER,
    DATA_SOURCE_SELECTION_CHANGE,
    DATA_SOURCE_SELECTION_CHANGE_ALL,
    DELETE_USER_DISABLED,
    DELETE_USER_INVITE_REVOKED,
    DIMENSION_FILTER_SELECTION_CHANGE,
    FETCH_SECTION_FAILED,
    FETCH_SECTION_REQUESTED,
    FETCH_SECTION_SUCCEEDED,
    HIDE_PAGE,
    MANAGE_PERMISSIONS,
    MANAGE_PERMISSIONS_FINISHED,
    METRIC_SELECTION_CHANGE,
    MODIFY_INVITATION_STATE,
    ORG_INVITES_LOADED,
    ORG_USERS_LOADED,
    RESET_SECTION_PAGINATION,
    SAVE_MEMBER_ERROR,
    SAVE_MEMBER_SUCCESS,
    SCREENS_SELECTION_TOGGLE,
    SDK_EVENT_SELECTION_CHANGE,
    SELECTED_TAB_INDEX_CHANGED,
    SET_SECTION_SEARCH,
    SET_SECTION_SORT,
    STEP_VALIDATION_STATUS_CHANGED,
    TEAMMANAGEMENT_PAGE_LOADED,
    UPDATE_AGENCY_SELECTION_FORM,
    UPDATE_APPS_LIST,
    UPDATE_DATA_SOURCE_DATA,
    UPDATE_FEATURES_DICT,
    UPDATE_PARTNERS_DATA,
    UPDATE_ROLE_SELECTION_FORM,
    UPDATE_SDK_EVENTS,
    UPDATE_SECTION_PERMISSIONS,
    UPDATE_SECTIONS_DICT,
    UPDATE_USER_REGISTRATION_FORM,
    SHOW_CONFIRM_CHANGES_DIALOG,
    CONFIRM_CHANGES_ACCEPTED,
    CONFIRM_CHANGES_REJECTED,
    SET_INITIAL_PERMISSION_SNAPSHOT,
} from './actions';
import { SECTIONS_PAGES_CONFIG } from './sections';
import {
    DEFAULT_AGENCY_SCREEN_SELECTION,
    MEMBER_TYPES,
    PAGE_MODES,
    ROLE_AGENCY,
    ROLE_STANDARD,
    SCREENS_REQUIRING_ALL_METRICS,
} from './utils';

update.extend('$auto', (value, object) => {
    return object ? update(object, value) : update({}, value);
});
update.extend('$autoArray', (value, object) => {
    return object ? update(object, value) : update([], value);
});

export const SECTION_KEYS = ['activeUsers', 'inactiveUsers', 'invites', 'agencies'];

export const SECTION_PAGE_SIZE = 10;

const emptySection = () => ({
    items: [],
    total: 0,
    // Total without the search filter applied. Used to render the tab badge counts so the
    // badge reflects the org-wide count rather than the currently-filtered subset. Only
    // updated by FETCH_SECTION_SUCCEEDED when the slice's search is empty.
    overallTotal: 0,
    offset: 0,
    limit: SECTION_PAGE_SIZE,
    hasMore: false,
    search: '',
    sortBy: 'name',
    sortDir: 'asc',
    isLoading: false,
    error: null,
    requestId: 0,
    loaded: false,
});

export const initialState = {
    pageLoaded: false,
    orgUsers: [],
    orgInvites: [],
    messages: {
        pending: [],
        active: [],
    },
    uans: {},
    partners: [],
    appsList: [],
    sdkEvents: null, // null = not loaded yet, [] = loaded but empty
    screensDict: {},
    messagesDefs: {},
    sectionsDict: {},
    page: {
        show: false,
        loading: false,
        selectedTabIndex: 0,
        mode: '',
        stepValidationStatus: {
            isValid: false,
            title: '',
        },
    },
    agencyDialog: {
        open: false,
    },
    editMemberId: null,
    userRegistrationForm: {
        firstName: {
            value: '',
        },
        lastName: {
            value: '',
        },
        email: {
            value: '',
        },
        description: {
            value: '',
        },
    },
    agencySelectionForm: {
        id: '',
        name: '',
    },
    dimensionFilters: {},
    dataRestrictions: {
        sdk_event: {
            values: [],
            addFuture: false,
        },
    },
    selectedMetrics: {
        values: [],
        addFuture: false,
    },
    roleSelectionForm: {
        role: ROLE_STANDARD,
    },
    screenSelectionForm: {
        selectedScreens: [],
        addFuture: false,
    },
    saveMemberError: '',
    activeMemberType: MEMBER_TYPES.USER,
    sectionPermissions: [],
    configurationRequiredDialog: {
        open: false,
        pages: [],
    },
    confirmChangesDialog: {
        open: false,
    },
    initialPermissionSnapshot: null,
    savedMemberName: '',
    // True while /agencies/self (the legacy un-paginated fetch that populates state.orgAgencies)
    // is in flight after a delete or permission save. The Add Agency button is gated on this
    // so the user can't open AgencyDialog against a stale agency list.
    agenciesLegacyFetching: false,
    activeUsers: emptySection(),
    inactiveUsers: emptySection(),
    invites: emptySection(),
    agencies: emptySection(),
};

const selectionToggleFunc = (action, idsKey = 'appIDs', items) => {
    let ret = items;
    if (action.value === false) {
        ret = [...items].filter(id => !action[idsKey].includes(id));
    }
    if (action.value === true) {
        ret = [...items, ...action[idsKey]];
    }
    return uniq(ret);
};

const teamManagement = (state = initialState, action) => {
    switch (action.type) {
        case TEAMMANAGEMENT_PAGE_LOADED: {
            return update(state, {
                pageLoaded: {
                    $set: true,
                },
            });
        }
        case ORG_USERS_LOADED: {
            return update(state, {
                orgUsers: {
                    $set: action.orgUsers,
                },
            });
        }
        case ORG_INVITES_LOADED: {
            return update(state, {
                orgInvites: {
                    $set: action.orgInvites,
                },
            });
        }
        case UPDATE_FEATURES_DICT: {
            return update(state, {
                screensDict: {
                    $set: action.featuresDict,
                },
            });
        }
        case UPDATE_SECTIONS_DICT: {
            return update(state, {
                sectionsDict: {
                    $set: action.sectionsDict,
                },
            });
        }
        case DELETE_USER_INVITE_REVOKED: {
            const { user } = action;
            const inviteIndex = state.orgInvites.findIndex(inv => inv.invite_id === user.invite_id);
            if (inviteIndex < 0) {
                return state;
            }
            return update(state, {
                orgInvites: {
                    $splice: [[inviteIndex, 1]],
                },
            });
        }
        case DELETE_USER_DISABLED: {
            const { user } = action;
            const userIndex = state.orgUsers.findIndex(ou => ou.user_id === user.user_id);
            return update(state, {
                orgUsers: {
                    $splice: [[userIndex, 1, { ...state.orgUsers[userIndex], status: 'Deactivated' }]],
                },
            });
        }
        case CLEAN_MESSAGES: {
            return update(state, {
                messages: Object.keys(state.messages).reduce((total, groupName) => {
                    total[groupName] = { $set: [] };
                    return total;
                }, {}),
            });
        }
        case ADD_MESSAGE_TO_GROUP: {
            const { group, message, messageType } = action;
            const guid = generateGuid();
            // Changing behaviour per product request. removing other notifications when a new one introduced
            const otherGroups = Object.keys(state.messages)
                .filter(m => m !== group)
                .reduce((total, groupName) => {
                    total[groupName] = {
                        $set: [],
                    };
                    return total;
                }, {});

            return update(state, {
                messages: {
                    [group]: {
                        $set: [guid],
                    },
                    ...otherGroups,
                },
                messagesDefs: {
                    [guid]: {
                        $set: {
                            message,
                            type: messageType,
                        },
                    },
                },
            });
        }
        case CREATE_NEW_MEMBER: {
            return update(state, {
                page: {
                    show: { $set: state.activeMemberType === MEMBER_TYPES.USER },
                    selectedTabIndex: { $set: 0 },
                    mode: { $set: PAGE_MODES.create },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                agencyDialog: {
                    open: { $set: state.activeMemberType === MEMBER_TYPES.AGENCY },
                },
                userRegistrationForm: {
                    $set: initialState.userRegistrationForm,
                },
                agencySelectionForm: {
                    $set: initialState.agencySelectionForm,
                },
                roleSelectionForm: {
                    role: {
                        $set: state.activeMemberType === MEMBER_TYPES.AGENCY ? ROLE_AGENCY : ROLE_STANDARD,
                    },
                },
                dimensionFilters: {
                    $set: {},
                },
                dataRestrictions: {
                    $set: initialState.dataRestrictions,
                },
                selectedMetrics: {
                    values: {
                        $set: [],
                    },
                    addFuture: {
                        $set: false,
                    },
                },
                screenSelectionForm: {
                    selectedScreens: {
                        $set: state.activeMemberType === MEMBER_TYPES.AGENCY ? DEFAULT_AGENCY_SCREEN_SELECTION : [],
                    },
                    addFuture: {
                        $set: false,
                    },
                },
                sectionPermissions: {
                    $set: [],
                },
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
            });
        }
        case HIDE_PAGE: {
            return update(state, {
                page: {
                    show: { $set: false },
                    loading: { $set: false },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                editMemberId: {
                    $set: null,
                },
                initialPermissionSnapshot: {
                    $set: null,
                },
                savedMemberName: {
                    $set: '',
                },
            });
        }
        case SELECTED_TAB_INDEX_CHANGED: {
            return update(state, {
                page: {
                    selectedTabIndex: { $set: action.selectedTabIndex },
                },
            });
        }
        case STEP_VALIDATION_STATUS_CHANGED: {
            return update(state, {
                page: {
                    stepValidationStatus: { $set: action.stepValidationStatus },
                },
            });
        }
        case CLOSE_AGENCY_DIALOG: {
            return update(state, {
                agencyDialog: {
                    open: { $set: false },
                },
            });
        }
        case MANAGE_PERMISSIONS: {
            const { partialState } = action;
            return update(state, {
                page: {
                    show: { $set: true },
                    selectedTabIndex: { $set: -1 },
                    mode: { $set: PAGE_MODES.edit },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                roleSelectionForm: {
                    $set: partialState.roleSelectionForm,
                },
                userRegistrationForm: {
                    $set: partialState.userRegistrationForm,
                },
                agencySelectionForm: {
                    $set: partialState.agencySelectionForm,
                },
                editMemberId: {
                    $set: partialState.editMemberId,
                },
                selectedMetrics: {
                    $set: partialState.selectedMetrics,
                },
                screenSelectionForm: {
                    $set: partialState.screenSelectionForm,
                },
                dimensionFilters: {
                    $set: partialState.dimensionFilters,
                },
                dataRestrictions: {
                    $set: partialState.dataRestrictions || initialState.dataRestrictions,
                },
                sectionPermissions: {
                    $set: partialState.sectionPermissions,
                },
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
                saveMemberError: {
                    $set: '',
                },
            });
        }
        case UPDATE_DATA_SOURCE_DATA: {
            return update(state, {
                uans: {
                    $set: action.data,
                },
            });
        }
        case UPDATE_PARTNERS_DATA: {
            return update(state, {
                partners: {
                    $set: action.partners,
                },
            });
        }
        case UPDATE_APPS_LIST: {
            return update(state, {
                appsList: {
                    $set: action.appsList,
                },
            });
        }
        case APP_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    app: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: selectionToggleFunc.bind(null, action, 'appIDs'),
                                },
                            },
                        },
                    },
                },
            });
        }
        case APP_SITE_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    app_site_id: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: selectionToggleFunc.bind(null, action, 'appSiteIDs'),
                                },
                            },
                        },
                    },
                },
            });
        }
        case SCREENS_SELECTION_TOGGLE: {
            return update(state, {
                screenSelectionForm: {
                    selectedScreens: {
                        $apply: selectionToggleFunc.bind(null, action, 'screenIds'),
                    },
                },
            });
        }
        case DATA_SOURCE_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    base_source: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: sources => {
                                        if (sources.includes(action.name) && action.value === false) {
                                            return [...sources].filter(id => id !== action.name);
                                        }
                                        if (!sources.includes(action.name) && action.value === true) {
                                            return [...sources, action.name];
                                        }
                                        return sources;
                                    },
                                },
                            },
                        },
                    },
                },
            });
        }
        case DATA_SOURCE_SELECTION_CHANGE_ALL: {
            return update(state, {
                dimensionFilters: {
                    base_source: {
                        $auto: {
                            values: {
                                $autoArray: {
                                    $apply: () => {
                                        if (action.value) {
                                            return action.allSources;
                                        } else {
                                            return [];
                                        }
                                    },
                                },
                            },
                        },
                    },
                },
            });
        }

        case DIMENSION_FILTER_SELECTION_CHANGE: {
            return update(state, {
                dimensionFilters: {
                    [action.name]: {
                        $auto: {
                            values: {
                                $set: action.values,
                            },
                        },
                    },
                },
            });
        }

        case METRIC_SELECTION_CHANGE: {
            const newState = update(state, {
                selectedMetrics: {
                    values: {
                        $apply: metrics => {
                            if (action.value === false) {
                                return [...metrics].filter(id => !action.metricID.includes(id));
                            }
                            if (action.value === true) {
                                return [...new Set([...metrics, ...action.metricID])];
                            }
                            return metrics;
                        },
                    },
                },
            });
            if (action.value === false) {
                return update(newState, {
                    screenSelectionForm: {
                        selectedScreens: {
                            $apply: screens => screens.filter(s => !SCREENS_REQUIRING_ALL_METRICS.includes(s)),
                        },
                    },
                });
            }
            return newState;
        }
        case UPDATE_USER_REGISTRATION_FORM: {
            return update(state, {
                userRegistrationForm: {
                    $set: action.fields,
                },
                saveMemberError: {
                    $set: '',
                },
            });
        }
        case UPDATE_AGENCY_SELECTION_FORM: {
            return update(state, {
                agencySelectionForm: {
                    $set: action.agency,
                },
                agencyDialog: {
                    open: { $set: false },
                },
                page: {
                    show: { $set: true },
                    selectedTabIndex: { $set: 0 },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
            });
        }
        case UPDATE_ROLE_SELECTION_FORM: {
            return update(state, {
                roleSelectionForm: {
                    role: {
                        $set: action.role,
                    },
                },
            });
        }
        case SAVE_MEMBER_ERROR: {
            return update(state, {
                saveMemberError: {
                    $set: action.error,
                },
                page: {
                    loading: { $set: false },
                },
            });
        }
        case SAVE_MEMBER_SUCCESS: {
            return update(state, {
                page: {
                    show: { $set: false },
                    loading: { $set: false },
                    selectedTabIndex: { $set: 0 },
                    mode: { $set: '' },
                    stepValidationStatus: {
                        $set: { isValid: false, title: '' },
                    },
                },
                editMemberId: {
                    $set: null,
                },
                savedMemberName: {
                    $set: action.savedMemberName || '',
                },
                configurationRequiredDialog: {
                    open: {
                        $set:
                            state.activeMemberType === MEMBER_TYPES.AGENCY &&
                            state.configurationRequiredDialog.pages.length > 0,
                    },
                },
            });
        }
        case MODIFY_INVITATION_STATE: {
            const { user, userState } = action;
            const inviteIndex = state.orgInvites.findIndex(inv => inv.invite_id === user.invite_id);
            if (inviteIndex < 0) {
                return state;
            }
            const newInvite = {
                ...state.orgInvites[inviteIndex],
                ...userState,
            };
            return update(state, {
                orgInvites: {
                    $splice: [[inviteIndex, 1, newInvite]],
                },
            });
        }
        case MANAGE_PERMISSIONS_FINISHED: {
            const viewAccessSectionNames = state.sectionPermissions
                .filter(
                    sectionPermission =>
                        sectionPermission.access_type === SECTION_ACCESS_TYPES.VIEW &&
                        !VIEW_ONLY_SECTIONS.includes(sectionPermission.name)
                )
                .map(sectionPermission => sectionPermission.name);

            const viewAccessPages = SECTIONS_PAGES_CONFIG.filter(({ cards }) =>
                viewAccessSectionNames.some(sectionName =>
                    cards.some(({ parts }) => parts.some(({ sectionNames }) => sectionNames.includes(sectionName)))
                )
            ).map(sectionPageConfig => sectionPageConfig.page);

            return update(state, {
                saveMemberError: {
                    $set: '',
                },
                page: {
                    loading: { $set: true },
                },
                configurationRequiredDialog: {
                    pages: {
                        $set: viewAccessPages,
                    },
                },
            });
        }
        case CLOSE_CONFIGURATION_REQUIRED_DIALOG: {
            return update(state, {
                configurationRequiredDialog: {
                    open: { $set: false },
                    pages: { $set: [] },
                },
            });
        }
        case ADD_FUTURE_CHANGED: {
            if (action.dataRestriction) {
                return update(state, {
                    dataRestrictions: {
                        [action.form]: {
                            $auto: {
                                addFuture: {
                                    $set: action.value,
                                },
                            },
                        },
                    },
                });
            }
            if (action.dimensionFilter) {
                return update(state, {
                    dimensionFilters: {
                        [action.form]: {
                            $auto: {
                                addFuture: {
                                    $set: action.value,
                                },
                            },
                        },
                    },
                });
            }

            if (action.form === 'selectedMetrics' && !action.value) {
                return update(state, {
                    [action.form]: {
                        addFuture: { $set: action.value },
                    },
                    screenSelectionForm: {
                        selectedScreens: {
                            $apply: s => s.filter(n => !SCREENS_REQUIRING_ALL_METRICS.includes(n)),
                        },
                    },
                });
            }

            return update(state, {
                [action.form]: {
                    addFuture: {
                        $set: action.value,
                    },
                },
            });
        }
        case ACTIVE_MEMBER_TYPE_CHANGED: {
            return update(state, {
                activeMemberType: {
                    $set: action.activeMemberType,
                },
            });
        }
        case UPDATE_SECTION_PERMISSIONS: {
            const newSectionPermissions = state.sectionPermissions.map(sectionPermission => ({
                ...sectionPermission,
                access_type: action.sectionNames.includes(sectionPermission.name)
                    ? action.accessType
                    : sectionPermission.access_type,
            }));

            for (const sectionName of action.sectionNames) {
                if (!newSectionPermissions.some(sectionPermission => sectionPermission.name === sectionName)) {
                    newSectionPermissions.push({
                        name: sectionName,
                        access_type: action.accessType,
                    });
                }
            }

            return update(state, {
                sectionPermissions: {
                    $set: newSectionPermissions,
                },
            });
        }
        case UPDATE_SDK_EVENTS: {
            return update(state, {
                sdkEvents: {
                    $set: action.events,
                },
            });
        }
        case SDK_EVENT_SELECTION_CHANGE: {
            return update(state, {
                dataRestrictions: {
                    sdk_event: {
                        values: {
                            $set: action.eventNames,
                        },
                    },
                },
            });
        }
        case SET_INITIAL_PERMISSION_SNAPSHOT:
            return update(state, {
                initialPermissionSnapshot: { $set: action.snapshot },
            });
        case SHOW_CONFIRM_CHANGES_DIALOG:
            return update(state, {
                confirmChangesDialog: { open: { $set: true } },
            });
        case CONFIRM_CHANGES_ACCEPTED:
        case CONFIRM_CHANGES_REJECTED:
            return update(state, {
                confirmChangesDialog: { open: { $set: false } },
            });
        case FETCH_SECTION_REQUESTED:
            return update(state, {
                [action.section]: {
                    isLoading: { $set: true },
                    requestId: { $set: action.requestId },
                    error: { $set: null },
                },
            });
        case FETCH_SECTION_SUCCEEDED: {
            if (state[action.section].requestId !== action.requestId) return state;
            const { items, total, offset, limit, has_more: hasMore } = action.response;
            const nextItems = action.mode === 'append' ? state[action.section].items.concat(items) : items;
            const sectionUpdate = {
                items: { $set: nextItems },
                total: { $set: total },
                offset: { $set: offset },
                limit: { $set: limit },
                hasMore: { $set: hasMore },
                isLoading: { $set: false },
                loaded: { $set: true },
            };
            // Gate overallTotal on the search that was sent with THIS request, not on
            // the slice's current search — the user may have typed since the fetch fired.
            if (!action.requestSearch) {
                sectionUpdate.overallTotal = { $set: total };
            }
            return update(state, { [action.section]: sectionUpdate });
        }
        case FETCH_SECTION_FAILED:
            if (state[action.section].requestId !== action.requestId) return state;
            return update(state, {
                [action.section]: {
                    isLoading: { $set: false },
                    error: { $set: action.error },
                },
            });
        case RESET_SECTION_PAGINATION:
            // Note: we DON'T clear items here — keeping the old page visible during the
            // refetch avoids a "No results" flash and stops unrelated sections from
            // flashing skeletons when one section is reloading. The next
            // FETCH_SECTION_SUCCEEDED replaces items wholesale.
            return update(state, {
                [action.section]: {
                    offset: { $set: 0 },
                    hasMore: { $set: false },
                    total: { $set: 0 },
                    // bump past any in-flight requestId so a late success/failure is dropped
                    requestId: { $set: 0 },
                },
            });
        case SET_SECTION_SEARCH:
            return update(state, {
                [action.section]: { search: { $set: action.search } },
            });
        case SET_SECTION_SORT:
            return update(state, {
                [action.section]: {
                    sortBy: { $set: action.sortBy },
                    sortDir: { $set: action.sortDir },
                },
            });
        case AGENCIES_LEGACY_FETCHING:
            return update(state, { agenciesLegacyFetching: { $set: !!action.isFetching } });
        case SET_SECTION_OVERALL_TOTAL:
            // Direct set of the unfiltered tab-badge count. Not requestId-gated, so it
            // survives even when a concurrent filtered (search) fetch supersedes the
            // section's requestId — e.g. the post-save auto-search after creating a row.
            return update(state, {
                [action.section]: { overallTotal: { $set: action.overallTotal } },
            });
        default:
            return state;
    }
};

export default teamManagement;
